import type { AssistantAction, AssistantCapabilities, AssistantConversation, AssistantExtraction, AssistantMessage } from "@wms/domain";
import { useAuthStore } from "../auth/store";
import { API_BASE, ApiError, api, refreshOnce } from "./client";

/** «ИИ Помощник» (ТЗ 29.10.2026): the calls of the assistant screen. The server decides everything. */
export const assistant = {
  capabilities: () => api.get<AssistantCapabilities>("/assistant/capabilities", { client: "web" }),
  conversation: (id: string) => api.get<AssistantConversation>(`/assistant/conversations/${id}`),
  /** The employee's own dialogs that got at least one message, the most recently used first (section 19). */
  list: () => api.get<AssistantConversation[]>("/assistant/conversations", { client: "web", limit: 50 }),
  start: (warehouseId: string | null) => api.post<AssistantConversation>("/assistant/conversations", { client: "web", warehouse_id: warehouseId }),
  setWarehouse: (id: string, warehouseId: string | null) => api.patch<AssistantConversation>(`/assistant/conversations/${id}`, { warehouse_id: warehouseId }),
  say: (id: string, text: string, channel: "text" | "voice", document?: string | null) =>
    api.post<{ messages: AssistantMessage[] }>(`/assistant/conversations/${id}/messages`, { text, channel, document: document || undefined }),
  execute: (actionId: string, clientRequestId: string) => api.post<AssistantAction>(`/assistant/actions/${actionId}/execute`, { client_request_id: clientRequestId }),
  action: (actionId: string) => api.get<AssistantAction>(`/assistant/actions/${actionId}`),
  cancel: (actionId: string) => api.post<AssistantAction>(`/assistant/actions/${actionId}/cancel`),
  extraction: (id: string) => api.get<AssistantExtraction>(`/assistant/extractions/${id}`),
  patchExtraction: (id: string, body: Record<string, unknown>) => api.patch<AssistantExtraction>(`/assistant/extractions/${id}`, body),
  create: (id: string, revision: number, clientRequestId: string) =>
    api.post<{ extraction: AssistantExtraction; action?: AssistantAction; duplicate?: boolean }>(`/assistant/extractions/${id}/create`, {
      revision,
      client_request_id: clientRequestId,
    }),
};

async function authorized(path: string, init: RequestInit, retry = true): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let response: Response;
  try {
    response = await fetch(API_BASE + path, { ...init, headers });
  } catch {
    throw new ApiError({ title: "Нет связи с сервером", status: 0, detail: "Нет связи с сервером: проверьте сеть и повторите", code: "network" });
  }
  if (response.status === 401 && retry && (await refreshOnce().catch(() => false))) return authorized(path, init, false);
  if (!response.ok) {
    const text = await response.text();
    let data = null;
    try {
      data = JSON.parse(text);
    } catch {
      /* not json */
    }
    throw new ApiError(data ?? { title: response.statusText, status: response.status });
  }
  return response;
}

/** The invoice photo or PDF goes to the server as is; the type is checked there by its signature. */
export async function uploadInvoice(conversationId: string, file: File) {
  const form = new FormData();
  form.append("conversation_id", conversationId);
  form.append("file", file);
  const response = await authorized("/assistant/sources", { method: "POST", body: form });
  return (await response.json()) as { source_id: string; duplicate: boolean; extraction: AssistantExtraction; messages: AssistantMessage[] };
}

/** The original of an invoice as an object URL (the owner's only; the caller revokes it). */
export async function invoiceFile(sourceUrl: string): Promise<{ url: string; type: string }> {
  const response = await authorized(sourceUrl.replace(/^\/api\/v1/, ""), { method: "GET" });
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

/** One key per press of a button: a repeat after a lost answer uses the same key (idempotent on the server). */
export function requestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
